import { defineMessages, injectIntl, useIntl } from 'react-intl'
import { getCurrentUser } from 'lib/utils'
import { pipe } from 'lib/functional'
import _ from 'lodash'
import moment from 'moment'

export const availableLocales = window.rwgps?.config.availableLocales ?? ['en']

const rwgpsToMomentLocale = locale => {
  const map = {
    'en': 'en-us',
    'es': 'es',
    'fr': 'fr',
    'ja': 'ja',
    'de': 'de',
    'nl': 'nl',
    'mz': 'x-pseudo'
  }
  return Object.entries(map)
    .filter(([rwgps, moment]) => availableLocales.includes(rwgps))
    .reduce((acc, [k, v]) => {
      acc[k] = v
      return acc
    }, {})[locale]
}

export const setMomentLocale = (override = null) => {
  let locale = 'en-us'
  const currentUser = getCurrentUser()
  if (override) {
    locale = rwgpsToMomentLocale(override)
  } else if (currentUser.id) {
    locale = rwgpsToMomentLocale(window?.rwgps.config.locale)
  }

  locale = locale ?? 'en-us'
  const { weeks_start_on: weeksStartOn } = (window.rwgps && window.rwgps.config.currentUser) || {}
  const updateDOW = () => moment.locale(locale, {
    week: {
      dow: weeksStartOn || 0
    }
  })

  if (locale !== 'en-us') {
    import('moment/locale/' + locale)
      .then(() => {
        updateDOW()
        moment.locale(locale)
      })
  } else {
    updateDOW()
    moment.locale(locale)
  }
}

// because intl.formatMessage is a lot to type over and over again
export const fm = (intl, message, values) => intl.formatMessage(message, values)

export const useFormatMessage = () => {
  const intl = useIntl()
  return intl.formatMessage
}

// utility component for re-using a message in multiple places without
// having to injectIntl in the parent
export const Msg = injectIntl(({ m, capitalized, startCase, lowerCase, values, intl }) => {
  const fns = []
  capitalized && fns.push(_.capitalize)
  startCase && fns.push(_.startCase)
  lowerCase && fns.push(_.lowerCase)

  return fns.length > 0
    ? pipe(...fns)(fm(intl, m, values))
    : fm(intl, m, values)
})

// alphabetize keys in this file, please
export const featureNames = defineMessages({
  analyze: {
    id: 'featureNames.analyze',
    defaultMessage: 'Analyze',
    description: 'Feature name for our Analyze page, a filterable and customizable view of all user activity'
  },
  calendar: {
    id: 'featureNames.calendar',
    defaultMessage: 'Calendar',
    description: "Feature name for our user Calendar, a calendar view of a user's rides and events"
  },
  challenges: {
    id: 'featureNames.challenges',
    defaultMessage: 'Challenges',
    description: 'Feature name for Challenges - a way for users to organize competitions'
  },
  collection: {
    id: 'featureNames.collection',
    defaultMessage: 'Collection',
    description: 'A single instance of our Collections feature'
  },
  collections: {
    id: 'featureNames.collections',
    defaultMessage: 'Collections',
    description: 'Feature name for our user Collections page, a place where users can organize their routes and rides'
  },
  dashboard: {
    id: 'featureNames.dashboard',
    defaultMessage: 'Dashboard',
    description: "Feature name for our user Dashboard, an at-a-glance look at all of a user's data on the site"
  },
  estimatedTime: {
    id: 'featureNames.estimatedTime',
    defaultMessage: 'estimated time',
    description: 'Feature name for a utility that estimates the time it will take a user to navigate a route'
  },
  events: {
    id: 'featureNames.events',
    defaultMessage: '{count, plural, =0 {events} one {event} other {events}}',
    description: 'Feature name for a product that allows groups to organize cycling events'
  },
  experiences: {
    id: 'featureNames.experiences',
    defaultMessage: '{count, plural, =0 {experiences} one {experience} other {experiences}}',
    description: 'Feature name for a product that allows users to download packaged routes and guides'
  },
  faq: {
    id: 'featureNames.faq',
    defaultMessage: 'FAQ',
    description: 'Stands for Frequently Asked Questions- a place where common questions are answered'
  },
  features: {
    id: 'featureNames.features',
    defaultMessage: 'Features',
    description: 'Page descibing the features available on the Ride with GPS platform'
  },
  find: {
    id: 'featureNames.find',
    defaultMessage: 'Find',
    description: 'Feature name for the Find search page, used to search for bike rides'
  },
  friends: {
    id: 'featureNames.friends',
    defaultMessage: 'Friends',
    description: 'Feature name for our Friends page, a feed of all friended user activity'
  },
  feed: {
    id: 'featureNames.feed',
    defaultMessage: 'Feed',
    description: 'Feature name for our Feed page, a feed of all followed user activity'
  },
  gear: {
    id: 'featureNames.gear',
    defaultMessage: 'Gear',
    description: 'Feature name for Gear feature'
  },
  globalHeatmap: {
    id: 'featureNames.globalHeatmap',
    defaultMessage: 'Global Heatmap',
    description: 'Feature name for Global Heatmap'
  },
  goals: {
    id: 'featureNames.goals',
    defaultMessage: 'Goals',
    description: 'Feature name for Goals feature'
  },
  heatmap: {
    id: 'featureNames.heatmap',
    defaultMessage: 'Heatmap',
    description: 'Feature name for our Heatmap: a representation of rides taken by our users'
  },
  help: {
    id: 'featureNames.help',
    defaultMessage: 'Help',
    description: 'Feature name for our Help portal, a comprehensive documentation resource for how to use the site'
  },
  home: {
    id: 'featureNames.home',
    defaultMessage: 'Home',
    description: 'A link to quickly access a user-defined home page'
  },
  integrations: {
    id: 'featureNames.integrations',
    defaultMessage: 'Integrations',
    description: 'Page with information on how to connect your account to other services'
  },
  journal: {
    id: 'featureNames.journal',
    defaultMessage: 'Journal',
    description: 'Page with dispatches from inside the company and our partners'
  },
  messages: {
    id: 'featureNames.messages',
    defaultMessage: 'Messages',
    description: 'Page where users can send text messages to each other'
  },
  milestones: {
    id: 'featureNames.milestones',
    defaultMessage: 'Milestones',
    description: 'Feature that allows the user to track progress towards a goal'
  },
  mobileApp: {
    id: 'featureNames.mobileApp',
    defaultMessage: 'Mobile App'
  },
  mobileRoutePlanner: {
    id: 'featureNames.mobileRoutePlanner',
    defaultMessage: 'Mobile Route Planner'
  },
  more: {
    id: 'featureNames.more',
    defaultMessage: 'More',
    description: 'Feature name for our More page, which contains various additional functionality not found on other pages'
  },
  newsAndPress: {
    id: 'featureNames.newsAndPress',
    defaultMessage: 'News & Press',
    description: 'Page with dispatches from inside the company and our partners'
  },
  organizations: {
    id: 'featureNames.organizations',
    defaultMessage: 'Organizations',
    description: 'Page with information about our Organizations accounts'
  },
  pointsOfInterest: {
    id: 'featureNames.pointsOfInterest',
    defaultMessage: '{count, plural, =0 {Points of Interest} one {Point of Interest} other {Points of Interest}}',
    description: 'Map markers indicating the position of something notable'
  },
  profile: {
    id: 'featureNames.profile',
    defaultMessage: 'Profile',
    description: "A user's presence on the site"
  },
  rideReports: {
    id: 'featureNames.rideReports',
    defaultMessage: 'Ride Reports',
    description: 'Feature name for the Ride Reports page, a showcase of user-written blog posts about interesting bike rides'
  },
  rides: {
    id: 'featureNames.rides',
    defaultMessage: 'Rides',
    description: "Feature name for our user Rides page, a searchable list of all of a user's recorded cycling rides"
  },
  routePlanner: {
    id: 'featureNames.routePlanner',
    defaultMessage: 'Route Planner',
    description: 'Feature name for the Route Planner tool, used to plan bike rides'
  },
  routes: {
    id: 'featureNames.routes',
    defaultMessage: 'Routes',
    description: "Feature name for our user Routes page, a searchable list of all of a user's planned and saved cycling routes"
  },
  routesAndRides: {
    id: 'featureNames.routesAndRides',
    defaultMessage: 'Routes & Rides',
    description: "A label for a view displaying a user's recent Routes and Rides"
  },
  search: {
    id: 'featureNames.search',
    defaultMessage: 'Search'
  },
  sendToDevice: {
    id: 'featureNames.sendToDevice',
    defaultMessage: 'Send to Device',
    description: "A feature that sends an asset to a user's device"
  },
  settings: {
    id: 'featureNames.settings',
    defaultMessage: 'Settings'
  },
  tags: {
    id: 'featureNames.tags',
    defaultMessage: '{count, plural, =0 {tags} one {tag} other {tags}}',
    description: 'A feature allowing users to tag their content'
  },
  upgrade: {
    id: 'featureNames.upgrade',
    defaultMessage: 'Upgrade',
    description: 'Page which allows a user to upgrade their account'
  },
  upload: {
    id: 'featureNames.upload',
    defaultMessage: 'Upload',
    description: 'Page used to upload ride data into our site'
  },
  webRoutePlanner: {
    id: 'featureNames.webRoutePlanner',
    defaultMessage: 'Web Route Planner'
  }
})

export const commonNouns = defineMessages({
  climbs: {
    id: 'commonNouns.climbs',
    defaultMessage: '{count, plural, =0 {climbs} one {climb} other {climbs}}'
  },
  comments: {
    id: 'commonNouns.comments',
    defaultMessage: '{count, plural, =0 {comments} one {comment} other {comments}}'
  },
  cuesheet: {
    id: 'commonNouns.cuesheet',
    defaultMessage: 'Cuesheet'
  },
  experiment: {
    id: 'commonNouns.experiment',
    defaultMessage: 'Experiment'
  },
  cycling: {
    id: 'commonNouns.cycling',
    defaultMessage: 'Cycling'
  },
  date: {
    id: 'commonNouns.date',
    defaultMessage: 'date',
    description: 'a specific calendar date'
  },
  descents: {
    id: 'commonNouns.descents',
    defaultMessage: '{count, plural, one {descent} other {descents}}'
  },
  description: {
    id: 'commonNouns.description',
    defaultMessage: 'description',
    description: 'Text explaining or giving an account of something'
  },
  details: {
    id: 'commonNouns.details',
    defaultMessage: 'Details'
  },
  distance: {
    id: 'commonNouns.distance',
    defaultMessage: 'distance',
    description: 'a distance value, either in miles or kilometers'
  },
  driving: {
    id: 'commonNouns.driving',
    defaultMessage: 'Driving'
  },
  elevation: {
    id: 'commonNouns.elevation',
    defaultMessage: 'elevation',
    description: 'an elevation value, either in meters or feet'
  },
  friends: {
    id: 'commonNouns.friends',
    defaultMessage: '{count, plural, =0 {friends} one {friend} other {friends}}'
  },
  goal: {
    id: 'commonNouns.goal',
    defaultMessage: 'goal',
    description: 'a user-created cycling goal (i.e. to ride more miles, do more elevation gain, etc)'
  },
  home: {
    id: 'commonNouns.home',
    defaultMessage: 'Home'
  },
  hours: {
    id: 'commonNouns.hours',
    defaultMessage: '{count, plural, =0 {hours} one {hour} other {hours}}',
    description: 'A measure of time, 60 minutes'
  },
  images: {
    id: 'commonNouns.images',
    defaultMessage: '{count, plural, =0 {images} one {image} other {images}}',
    description: 'Visual representation of something, a photo'
  },
  items: {
    id: 'commonNouns.items',
    defaultMessage: '{count, plural, one {item} other {items}}'
  },
  laps: {
    id: 'commonNouns.laps',
    defaultMessage: '{count, plural, one {lap} other {laps}}'
  },
  logo: {
    id: 'commonNouns.logo',
    defaultMessage: 'Logo'
  },
  map: {
    id: 'commonNouns.map',
    defaultMessage: 'Map'
  },
  metrics: {
    id: 'commonNouns.metrics',
    defaultMessage: 'metrics',
    description: 'Measurements'
  },
  movingTime: {
    id: 'commonNouns.movingTime',
    defaultMessage: 'moving time',
    description: 'Duration that a user was moving during a bike rides, in hours/minutes/seconds'
  },
  name: {
    id: 'commonNouns.name',
    defaultMessage: 'name',
    description: 'A word or words used to refer to something or someone'
  },
  options: {
    id: 'commonNouns.options',
    defaultMessage: 'Options',
    description: 'An array of choices'
  },
  overview: {
    id: 'commonNouns.overview',
    defaultMessage: 'Overview',
    description: 'A general summary of something'
  },
  photos: {
    id: 'commonNouns.photos',
    defaultMessage: '{count, plural, =0 {photos} one {photo} other {photos}}'
  },
  rides: {
    id: 'commonNouns.rides',
    defaultMessage: '{count, plural, one {ride} other {rides}}',
    description: 'a user-uploaded bicycle ride recording'
  },
  routes: {
    id: 'commonNouns.routes',
    defaultMessage: '{count, plural, =0 {routes} one {route} other {routes}}',
    description: 'a user-created bicycle route, planned using the route planner'
  },
  segments: {
    id: 'commonNouns.segments',
    defaultMessage: '{count, plural, one {segment} other {segments}}'
  },
  stats: {
    id: 'commonNouns.stats',
    defaultMessage: 'stats',
    description: 'Numerical information. Statistics.'
  },
  title: {
    id: 'commonNouns.title',
    defaultMessage: 'title',
    description: 'A name for something created by a user'
  },
  trips: {
    id: 'commonNouns.trips',
    defaultMessage: '{count, plural, one {trip} other {trips}}',
    description: 'A user-uploaded bicycle trip recording'
  },
  units: {
    id: 'commonNouns.units',
    defaultMessage: 'Units'
  },
  user: {
    id: 'commonNouns.user',
    defaultMessage: 'user',
    description: 'Someone who uses Ride with GPS'
  },
  visibility: {
    id: 'commonNouns.visibility',
    defaultMessage: 'Visibility',
    description: 'The degree to which something can be seen'
  },
  walking: {
    id: 'commonNouns.walking',
    defaultMessage: 'Walking'
  }
})

export const commonVerbs = defineMessages({
  accept: {
    id: 'commonVerbs.accept',
    defaultMessage: 'accept',
    description: 'To give consent'
  },
  add: {
    id: 'commonVerbs.add',
    defaultMessage: 'Add',
    description: 'To join to something else'
  },
  added: {
    id: 'commonVerbs.added',
    defaultMessage: 'added',
    description: 'To join to something else, in the past'
  },
  apply: {
    id: 'commonVerbs.apply',
    defaultMessage: 'apply',
    description: 'To be relevant'
  },
  archive: {
    id: 'commonVerbs.archive',
    defaultMessage: 'archive',
    description: 'To store information'
  },
  cancel: {
    id: 'commonVerbs.cancel',
    defaultMessage: 'cancel',
    description: 'The act of stopping an action'
  },
  delete: {
    id: 'commonVerbs.delete',
    defaultMessage: 'Delete',
    description: 'To remove or destroy, especially information'
  },
  dismiss: {
    id: 'commonVerbs.dismiss',
    defaultMessage: 'dismiss',
    description: 'To send away'
  },
  downloaded: {
    id: 'commonVerbs.downloaded',
    defaultMessage: 'downloaded',
    description: 'Past tense of download'
  },
  edit: {
    id: 'commonVerbs.edit',
    defaultMessage: 'Edit',
    description: 'The act of changing information'
  },
  editing: {
    id: 'commonVerbs.editing',
    defaultMessage: 'editing',
    description: 'Present participle form of edit'
  },
  follow: {
    id: 'commonVerbs.follow',
    defaultMessage: 'follow',
    description: 'An action that causes the user to be notified of ongoing activity'
  },
  followRoads: {
    id: 'commonVerbs.followRoads',
    defaultMessage: 'Follow Roads',
    description: 'An action that causes the user to be notified of ongoing activity'
  },
  ignore: {
    id: 'commonVerbs.ignore',
    defaultMessage: 'ignore',
    description: 'To refuse to acknowledge-- to ignore a message'
  },
  import: {
    id: 'commonVerbs.import',
    defaultMessage: 'Import',
    description: 'The act of bringing something in'
  },
  pin: {
    id: 'commonVerbs.pin',
    defaultMessage: 'Pin',
    description: 'An action that adds an item to a list of favorites'
  },
  pinned: {
    id: 'commonVerbs.pinned',
    defaultMessage: 'pinned',
    description: 'Past tense of pin'
  },
  remove: {
    id: 'commonVerbs.remove',
    defaultMessage: 'remove',
    description: 'To take something away'
  },
  save: {
    id: 'commonVerbs.save',
    defaultMessage: 'Save',
    description: 'The act of storing information'
  },
  send: {
    id: 'commonVerbs.send',
    defaultMessage: 'Send'
  },
  sentToDevice: {
    id: 'commonVerbs.sentToDevice',
    defaultMessage: 'sent to device',
    description: 'The user has previously downloaded a route to their device'
  },
  share: {
    id: 'commonVerbs.share',
    defaultMessage: 'Share',
    description: 'To share with a friend, or on social media'
  },
  unarchive: {
    id: 'commonVerbs.unarchive',
    defaultMessage: 'unarchive',
    description: 'To retrieve something from an archive'
  },
  unfollow: {
    id: 'commonVerbs.unfollow',
    defaultMessage: 'unfollow',
    description: 'An action that causes the user to stop being notified of an activity'
  },
  unpin: {
    id: 'commonVerbs.unpin',
    defaultMessage: 'Unpin',
    description: 'An action that removes an item from a list of favorites'
  },
  unpublish: {
    id: 'commonVerbs.unpublish',
    defaultMessage: 'Unpublish'
  },
  uploaded: {
    id: 'commonVerbs.uploaded',
    defaultMessage: 'uploaded',
    description: 'Past tense of upload'
  }
})

export const goalTypeLabels = defineMessages({
  distance: {
    id: 'goalTypeLabels.distance',
    defaultMessage: 'Distance',
    description: 'Label for a user-created Distance goal'
  },
  elevation_gain: {
    id: 'goalTypeLabels.elevation_gain',
    defaultMessage: 'Elevation',
    description: 'Label for a user-created Elevation Gain goal'
  },
  leaderboard: {
    id: 'goalTypeLabels.leaderboard',
    defaultMessage: 'Leaderboard',
    description: 'Label for a user-created Leaderboard goal, where the user competes with friends for a given milestone'
  },
  moving_time: {
    id: 'goalTypeLabels.moving_time',
    defaultMessage: 'Moving Time',
    description: 'Label for a user-created Moving Time goal'
  }
})

export const commonDateStrings = defineMessages({
  date: {
    id: 'commonDateStrings.date',
    defaultMessage: 'date',
    description: 'A calendar date'
  },
  endDate: {
    id: 'commonDateStrings.endDate',
    defaultMessage: 'End Date',
    description: 'String for identifying the end date of an event or goal'
  },
  startDate: {
    id: 'commonDateStrings.startDate',
    defaultMessage: 'Start Date',
    description: 'String for identifying the start date of an event or goal'
  }
})

export const commonActions = defineMessages({
  copied: {
    id: 'commonActions.copied',
    defaultMessage: 'Copied!',
    description: "A success message once information has been copied to the user's clipboard"
  },
  copyLink: {
    id: 'commonActions.copyLink',
    defaultMessage: 'Copy Link',
    description: "Copy information to the user's clipboard"
  },
  copyToMyRoutes: {
    id: 'commonActions.copyToMyRoutes',
    defaultMessage: 'Copy to My Routes'
  },
  exportCSV: {
    id: 'commonActions.exportCSV',
    defaultMessage: 'Export CSV',
    description: 'A tool allowing the user to export information in Comma Separated Value format'
  },
  exportCSVDesc: {
    id: 'commonActions.exportCSVDesc',
    defaultMessage: 'Export CSV of all Ride Data',
    description: 'A description of an export tool'
  },
  getCustomEmbed: {
    id: 'commonActions.getCustomEmbed',
    defaultMessage: 'Get Custom Embed',
    description: 'Allows the user to access an embeddable version of their content'
  },
  loadMore: {
    id: 'commonActions.loadMore',
    defaultMessage: 'Load More'
  }
})

export const commonMetrics = defineMessages({
  ascentTime: {
    id: 'commonMetrics.ascentTime',
    defaultMessage: 'Ascent Time',
    description: 'Time spent going uphill'
  },
  averageSpeed: {
    id: 'commonMetrics.averageSpeed',
    defaultMessage: 'average speed'
  },
  averageWatts: {
    id: 'commonMetrics.averageWatts',
    defaultMessage: 'average watts'
  },
  avgCadence: {
    id: 'commonMetrics.avgCadence',
    defaultMessage: 'Avg Cadence',
    description: 'Average cadance, abbreviated'
  },
  avgElevation: {
    id: 'commonMetrics.avgElevation',
    defaultMessage: 'Avg Elevation',
    description: 'The average elevation on a ride'
  },
  avgGrade: {
    id: 'commonMetrics.avgGrade',
    defaultMessage: 'Avg Grade',
    description: 'Average grade, abbreviated'
  },
  avgHR: {
    id: 'commonMetrics.avgHR',
    defaultMessage: 'Avg Heart Rate',
    description: 'Average heart rate for a ride'
  },
  avgSpeed: {
    id: 'commonMetrics.avgSpeed',
    defaultMessage: 'Avg Speed',
    description: 'Average speed, abbreviated'
  },
  avgPower: {
    id: 'commonMetrics.avgPower',
    defaultMessage: 'Avg Power',
    description: 'Average power, abbreviated'
  },
  avgPowerEst: {
    id: 'commonMetrics.avgPowerEst',
    defaultMessage: 'Avg Power (estimated)',
    description: 'Average estimated power, abbreviated'
  },
  avgWatts: {
    id: 'commonMetrics.avgWatts',
    defaultMessage: 'Avg Watts',
    description: 'Average watts, abbreviated'
  },
  avgWattsEst: {
    id: 'commonMetrics.avgWattsEst',
    defaultMessage: 'Avg Watts (estimated)',
    description: 'Estimated average watts, abbreviated'
  },
  bpm: {
    id: 'commonMetrics.bpm',
    defaultMessage: 'bpm',
    description: 'abbreviation of Beats Per Minute, an expression of heart rate'
  },
  cad: {
    id: 'commonMetrics.cad',
    defaultMessage: 'Cad',
    description: 'Abbreviation for cadance'
  },
  calories: {
    id: 'commonMetrics.calories',
    defaultMessage: 'calories'
  },
  caloriesBurned: {
    id: 'commonMetrics.caloriesBurned',
    defaultMessage: 'calories burned'
  },
  category: {
    id: 'commonMetrics.category',
    defaultMessage: 'category',
    description: 'Refers to UCI category, a way of referring to the difficulty of a climb'
  },
  createdOn: {
    id: 'commonMetrics.createdOn',
    defaultMessage: 'Created On'
  },
  descentTime: {
    id: 'commonMetrics.descentTime',
    defaultMessage: 'Descent Time',
    description: 'Time spent going downhill'
  },
  distance: {
    id: 'commonMetrics.distance',
    defaultMessage: 'Distance'
  },
  duration: {
    id: 'commonMetrics.duration',
    defaultMessage: 'duration'
  },
  ele: {
    id: 'commonMetrics.ele',
    defaultMessage: 'Ele',
    description: 'Elevation, abbreviated'
  },
  elevation: {
    id: 'commonMetrics.elevation',
    defaultMessage: 'Elevation'
  },
  elevationGain: {
    id: 'commonMetrics.elevationGain',
    defaultMessage: 'elevation gain'
  },
  elevationLoss: {
    id: 'commonMetrics.elevationLoss',
    defaultMessage: 'elevation loss'
  },
  endElevation: {
    id: 'commonMetrics.endElevation',
    defaultMessage: 'End Elevation'
  },
  endTime: {
    id: 'commonMetrics.endTime',
    defaultMessage: 'End Time'
  },
  grade: {
    id: 'commonMetrics.grade',
    defaultMessage: 'Grade',
    description: 'An expression of steepness'
  },
  heartRate: {
    id: 'commonMetrics.heartRate',
    defaultMessage: 'Heart Rate'
  },
  heartRateZones: {
    id: 'commonMetrics.heartRateZones',
    defaultMessage: '{count, plural, one {Heart Rate Zone} other {Heart Rate Zones}}'
  },
  hr: {
    id: 'commonMetrics.hr',
    defaultMessage: 'HR',
    description: 'Abbreviation for Heart Rate'
  },
  hrZone: {
    id: 'commonMetrics.hrZones',
    defaultMessage: 'HR Zone {number}',
    description: 'Abbreviation for Heart Rate Zone'
  },
  hoursRidden: {
    id: 'commonMetrics.hoursRidden',
    defaultMessage: '{count, plural, =0 {hours ridden} one {hour ridden} other {hours ridden}}',
    description: 'Hours spent riding a bike'
  },
  length: {
    id: 'commonMetrics.length',
    defaultMessage: 'length'
  },
  maxCadence: {
    id: 'commonMetrics.maxCadence',
    defaultMessage: 'Max Cadence',
    description: 'The maximum recorded cadance (in revolutions per minute) on a ride'
  },
  maxElevation: {
    id: 'commonMetrics.maxElevation',
    defaultMessage: 'Max Elevation',
    description: 'The maximum elevation on a ride'
  },
  maxGrade: {
    id: 'commonMetrics.maxGrade',
    defaultMessage: 'Max Grade',
    description: 'The maximum grade (steepness) on a route or ride'
  },
  maxHR: {
    id: 'commonMetrics.maxHR',
    defaultMessage: 'Max Heart Rate',
    description: 'Maximum recorded heart rate on a ride'
  },
  maxSpeed: {
    id: 'commonMetrics.maxSpeed',
    defaultMessage: 'Max Speed',
    description: 'The maximum measured speed (velocity) on a ride'
  },
  maxWatts: {
    id: 'commonMetrics.maxWatts',
    defaultMessage: 'Max Watts',
    description: 'The maximum measured wattage (Kj) on a ride'
  },
  minCadence: {
    id: 'commonMetrics.minCadence',
    defaultMessage: 'Min Cadence',
    description: 'The minimum recorded cadance (in revolutions per minute) on a ride'
  },
  minElevation: {
    id: 'commonMetrics.minElevation',
    defaultMessage: 'Min Elevation',
    description: 'The minimum elevation on a ride'
  },
  minGrade: {
    id: 'commonMetrics.minGrade',
    defaultMessage: 'Min Grade',
    description: 'The minimum grade (steepness) on a route or ride'
  },
  minHR: {
    id: 'commonMetrics.minHR',
    defaultMessage: 'Min Heart Rate',
    description: 'Minimum recorded heart rate on a ride'
  },
  minSpeed: {
    id: 'commonMetrics.minSpeed',
    defaultMessage: 'Min Speed',
    description: 'The minimum measured speed (velocity) on a ride'
  },
  movingPace: {
    id: 'commonMetrics.movingPace',
    defaultMessage: 'Moving Pace',
    description: 'An alternate measurement of velocity, in time per unit of distance'
  },
  movingTime: {
    id: 'commonMetrics.movingTime',
    defaultMessage: 'Moving Time'
  },
  pace: {
    id: 'commonMetrics.pace',
    defaultMessage: 'Pace',
    description: 'An alternate measurement of velocity, in time per unit of distance'
  },
  power: {
    id: 'commonMetrics.power',
    defaultMessage: 'Power'
  },
  rpm: {
    id: 'commonMetrics.rpm',
    defaultMessage: 'rpm',
    description: 'Abbreviation for revolutions per minute'
  },
  speed: {
    id: 'commonMetrics.speed',
    defaultMessage: 'speed'
  },
  startElevation: {
    id: 'commonMetrics.startElevation',
    defaultMessage: 'Start Elevation'
  },
  startTime: {
    id: 'commonMetrics.startTime',
    defaultMessage: 'Start Time'
  },
  stoppedTime: {
    id: 'commonMetrics.stoppedTime',
    defaultMessage: 'Stopped Time'
  },
  temp: {
    id: 'commonMetrics.temp',
    defaultMessage: 'Temp',
    description: 'Abbreviation for Temperature'
  },
  time: {
    id: 'commonMetrics.time',
    defaultMessage: 'Time'
  },
  totalDuration: {
    id: 'commonMetrics.totalDuration',
    defaultMessage: 'Total Duration'
  },
  updatedOn: {
    id: 'commonMetrics.updatedOn',
    defaultMessage: 'Updated On'
  },
  vam: {
    id: 'commonMetrics.vam',
    defaultMessage: 'VAM',
    description: 'An abbreviation for the Italian term velocità ascensionale media, meaning average climbing speed. A common cycling statistic.'
  },
  watts: {
    id: 'commonMetrics.watts',
    defaultMessage: 'watts'
  },
  wattsEst: {
    id: 'commonMetrics.wattsEst',
    defaultMessage: 'Watts (estimated)'
  },
  work: {
    id: 'commonMetrics.work',
    defaultMessage: 'Work',
    description: 'A measurement of energy expended during an activity, in kilojoules'
  },
  zone: {
    id: 'commonMetrics.zone',
    defaultMessage: 'zone'
  }
})

export const commonMessages = defineMessages({
  and: {
    id: 'commonMessages.and',
    defaultMessage: 'and'
  },
  asc: {
    id: 'commonMessages.asc',
    defaultMessage: 'Ascending'
  },
  byline: {
    id: 'commonMessages.byline',
    defaultMessage: 'By {author}'
  },
  changesSaved: {
    id: 'commonMessages.changesSaved',
    defaultMessage: 'Changes Saved'
  },
  collapse: {
    id: 'commonMessages.collapse',
    defaultMessage: 'Collapse {list}'
  },
  desc: {
    id: 'commonMessages.desc',
    defaultMessage: 'Descending'
  },
  dropPhotosBottom: {
    id: 'commonMessages.dropPhotosBottom',
    defaultMessage: 'You can also use the {fileBrowser}',
    description: 'A system dialog that allows the user to chooose a file from their computer'
  },
  dropPhotosFileBrowser: {
    id: 'commonMessages.dropPhotosFileBrowser',
    defaultMessage: 'File Browser'
  },
  dropPhotosTop: {
    id: 'commonMessages.dropPhotosTop',
    defaultMessage: 'Drop photos for your {objectType} here'
  },
  errorBoundary: {
    id: 'commonMessages.errorBoundary',
    defaultMessage: 'We encountered an error while loading this item.'
  },
  errorBoundaryReload: {
    id: 'commonMessages.errorBoundaryReload',
    defaultMessage: 'Please reload the page and try again. If you continue to experience this error, please <a>let us know</a>.'
  },
  errorMessage: {
    id: 'commonMessages.errorMessage',
    defaultMessage: 'Oops, we encountered an error while {action} {operand}. Please try again.'
  },
  errorMessageGeneric: {
    id: 'commonMessages.errorMessageGeneric',
    defaultMessage: 'Oops, we encountered an error! Please try again.'
  },
  getInspired: {
    id: 'commonMessages.getInspired',
    defaultMessage: 'Get Inspired'
  },
  loading: {
    id: 'commonMessages.loading',
    defaultMessage: 'Loading'
  },
  no: {
    id: 'commonMessages.no',
    defaultMessage: 'No'
  },
  noMoreItems: {
    id: 'commonMessages.noMoreItems',
    defaultMessage: 'No more items to display'
  },
  noResults: {
    id: 'commonMessages.noResults',
    defaultMessage: 'No results'
  },
  noItemsMatched: {
    id: 'commonMessages.noItemsMatched',
    defaultMessage: 'No items matched your search'
  },
  off: {
    id: 'commonMessages.off',
    defaultMessage: 'off',
    description: 'The opposite of on'
  },
  on: {
    id: 'commonMessages.on',
    defaultMessage: 'on',
    description: 'The opposite of off'
  },
  or: {
    id: 'commonMessages.or',
    defaultMessage: 'or'
  },
  saved: {
    id: 'commonMessages.saved',
    defaultMessage: 'Saved'
  },
  saving: {
    id: 'commonMessages.saving',
    defaultMessage: 'Saving'
  },
  shareErrorMessage: {
    id: 'commonMessages.shareErrorMessage',
    defaultMessage: 'Oops - something went wrong. Please try again.'
  },
  showAll: {
    id: 'commonMessages.showAll',
    defaultMessage: 'Show All {list}'
  },
  showingResults: {
    id: 'commonMessages.showingResults',
    defaultMessage: 'Showing 1 - {length} of {totalCount} results'
  },
  yes: {
    id: 'commonMessages.yes',
    defaultMessage: 'Yes'
  },
  your: {
    id: 'commonMessages.your',
    defaultMessage: 'Your {things}'
  },
  zoomIn: {
    id: 'commonMessages.zoomIn',
    defaultMessage: 'Zoom in {zoomable}'
  },
  zoomOut: {
    id: 'commonMessages.zoomOut',
    defaultMessage: 'Zoom out {zoomable}'
  }
})

export const commonTRouteMessages = defineMessages({
  dateCreated: {
    id: 'commonTRouteMessages.dateCreated',
    defaultMessage: 'Date Created'
  },
  hasCuesheet: {
    id: 'commonTRouteMessages.hasCuesheet',
    defaultMessage: 'Has Cuesheet',
    description: 'A message indicating that a route includes turn-by-turn directions'
  },
  lastUpdated: {
    id: 'commonTRouteMessages.lastUpdated',
    defaultMessage: 'Last Updated'
  },
  startLocation: {
    id: 'commonTRouteMessages.startLocation',
    defaultMessage: 'Start Location',
    description: 'A label for the departure of a route or ride'
  }
})

const searchResultsTextMessages = defineMessages({
  outer: {
    id: 'SearchableCard.showingWithMore',
    defaultMessage: 'Showing {oneToX} {resultsCount} results'
  },
  inner: {
    id: 'SearchableCard.optional',
    defaultMessage: '1 - {resultsLength} of'
  }
})

export const searchResultsText = (resultsLength, resultsCount) => (
  Msg({
    m: searchResultsTextMessages.outer,
    values: {
      resultsCount,
      oneToX: resultsLength !== resultsCount && Msg({
        m: searchResultsTextMessages.inner,
        values: { resultsLength }
      })
    }
  })
)

export const commonCTAs = defineMessages({
  back: {
    id: 'commonCTAs.back',
    defaultMessage: 'back',
    description: 'Return to the previous step'
  },
  backToAccount: {
    id: 'commonCTAs.backToAccount',
    defaultMessage: 'Back to Account'
  },
  cancel: {
    id: 'commonCTAs.cancel',
    defaultMessage: 'Cancel',
    description: 'Stop a process'
  },
  close: {
    id: 'commonCTAs.close',
    defaultMessage: 'Close'
  },
  confirm: {
    id: 'commonCTAs.confirm',
    defaultMessage: 'Confirm'
  },
  done: {
    id: 'commonCTAs.done',
    defaultMessage: 'done',
    description: 'Finish a process'
  },
  editProfile: {
    id: 'commonCTAs.editProfile',
    defaultMessage: 'Edit Profile'
  },
  fewer: {
    id: 'commonCTAs.fewer',
    defaultMessage: 'Fewer {things}',
    description: 'A button which will show fewer items in a list'
  },
  go: {
    id: 'commonCTAs.go',
    defaultMessage: 'Go'
  },
  goBack: {
    id: 'commonCTAs.goBack',
    defaultMessage: 'Go Back'
  },
  gotIt: {
    id: 'commonCTAs.gotIt',
    defaultMessage: 'Got It!'
  },
  helpPage: {
    id: 'commonCTAs.helpPage',
    defaultMessage: 'help page'
  },
  learnMore: {
    id: 'commonCTAs.learnMore',
    defaultMessage: 'Learn More',
    description: 'A link to a feature marketing page'
  },
  learnMorePremium: {
    id: 'commonCTAs.learnMorePremium',
    defaultMessage: 'Learn More About Premium',
    description: 'A link to a premium feature marketing page'
  },
  loadMore: {
    id: 'commonCTAs.loadMore',
    defaultMessage: 'Load More',
    description: 'Load the next page of results'
  },
  more: {
    id: 'commonCTAs.more',
    defaultMessage: 'More {things}',
    description: 'A button which will show more items in a list'
  },
  next: {
    id: 'commonCTAs.next',
    defaultMessage: 'next',
    description: 'Advance to the next step'
  },
  ok: {
    id: 'commonCTAs.ok',
    defaultMessage: 'OK'
  },
  previous: {
    id: 'commonCTAs.previous',
    defaultMessage: 'previous',
    description: 'Return to the previous step'
  },
  showLess: {
    id: 'commonCTAs.showLess',
    defaultMessage: 'Show Less',
    description: 'Display fewer pieces of content, generally in a list'
  },
  showMore: {
    id: 'commonCTAs.showMore',
    defaultMessage: 'Show More',
    description: 'Display more pieces of content, generally in a list'
  },
  signUp: {
    id: 'commonCTAs.signUp',
    defaultMessage: 'sign up'
  },
  skip: {
    id: 'commonCTAs.skip',
    defaultMessage: 'skip',
    description: 'Jump over the next step'
  },
  startFreeTrial: {
    id: 'commonCTAs.startFreeTrial',
    defaultMessage: 'Start Free Trial'
  },
  upgradeNow: {
    id: 'commonCTAs.upgradeNow',
    defaultMessage: 'Upgrade Now'
  }
})

export const accountLevels = defineMessages({
  basic: {
    id: 'accountLevels.basic',
    defaultMessage: 'Basic'
  },
  premium: {
    id: 'accountLevels.premium',
    defaultMessage: 'Premium'
  }
})

export const commonUnits = defineMessages({
  feet: {
    id: 'commonUnits.feet',
    defaultMessage: '{count, plural, =0 {feet} one {foot} other {feet}}'
  },
  imperial: {
    id: 'commonUnits.imperial',
    defaultMessage: 'Imperial',
    description: 'As in the imperial unit system'
  },
  kilometers: {
    id: 'commonUnits.kilometers',
    defaultMessage: '{count, plural, =0 {kilometers} one {kilometer} other {kilometers}}'
  },
  meters: {
    id: 'commonUnits.meters',
    defaultMessage: '{count, plural, =0 {meters} one {meter} other {meters}}'
  },
  metric: {
    id: 'commonUnits.metric',
    defaultMessage: 'Metric',
    description: 'As in the metric unit system'
  },
  miles: {
    id: 'commonUnits.miles',
    defaultMessage: '{count, plural, =0 {miles} one {mile} other {miles}}'
  }
})

export const privacyLevels = defineMessages({
  friendsOnly: {
    id: 'privacyLevels.friendsOnly',
    defaultMessage: 'Friends only'
  },
  rmOnly: {
    id: 'privacyLevels.rmOnly',
    defaultMessage: 'Route Managers Only'
  },
  managers: {
    id: 'privacyLevels.managers',
    defaultMessage: 'Managers'
  },
  mOnly: {
    id: 'privacyLevels.mOnly',
    defaultMessage: 'Members Only'
  },
  members: {
    id: 'privacyLevels.members',
    defaultMessage: 'Members'
  },
  privacy: {
    id: 'privacyLevels.privacy',
    defaultMessage: 'Privacy'
  },
  private: {
    id: 'privacyLevels.private',
    defaultMessage: 'Private'
  },
  public: {
    id: 'privacyLevels.public',
    defaultMessage: 'Public'
  },
  clubCollectionPublic: {
    id: 'privacyLevels.clubCollectionPublic',
    defaultMessage: 'Public - Promote to the entire Ride with GPS community'
  }
})

export const timeLabels = defineMessages({
  hhmm: {
    id: 'timeLabels.hhmm',
    defaultMessage: 'hh:mm',
    description: 'A label for a time duration; 01:10 hh:mm meaning one hour, ten minutes'
  }
})
